import { Bugfender } from '@bugfender/sdk';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { addDocument } from '@/api/document';
import { getHolderDetailAndRequiredDocumentsRequest } from '@/api/operation';

import { ROUTE } from '@/constants/routes';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { setOperationData } from '@/redux/operation';
import {
  selectAccountTypes,
  selectUserHolderJointMinor,
} from '@/redux/user-data';

import { DocumentSection, DocumentType } from '@/types/document';

export function useMinorIdentityUpload(
  docType: DocumentType.BirthCertificate | DocumentType.FamilyBook,
) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector(selectLoggedInUser);
  const holderJointMinor = useAppSelector(selectUserHolderJointMinor);
  const accountTypes = useAppSelector(selectAccountTypes);

  const minorClientId = holderJointMinor?.clientId;
  const operationId = user?.operationId;

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resetError = useCallback(() => setError(null), []);

  const upload = useCallback(
    async (imageDataUrl: string) => {
      try {
        setIsUploading(true);
        setError(null);
        if (
          docType === DocumentType.FamilyBook &&
          (!operationId || !minorClientId)
        ) {
          throw new Error(
            'Missing minor holder details for Family Book upload',
          );
        }

        // Convert data URL to File
        const response = await fetch(imageDataUrl);
        const blob = await response.blob();
        const file = new File(
          [blob],
          `${docType === DocumentType.FamilyBook ? 'family-book' : 'birth-certificate'}.jpg`,
          {
            type: 'image/jpeg',
          },
        );

        // Upload document
        await addDocument({
          file,
          section: DocumentSection.Identity,
          type: docType,
          clientId: minorClientId,
        });

        // Refresh the minor's operation state so ProtectedRoute
        // sees the updated document statuses.
        if (operationId && minorClientId !== undefined) {
          try {
            const minorData = await getHolderDetailAndRequiredDocumentsRequest(
              operationId,
              minorClientId,
            );
            dispatch(
              setOperationData({
                user: minorData.user,
                requiredSections: minorData.requiredSections,
                accountTypes,
              }),
            );
          } catch (e) {
            Bugfender.error(
              'Failed to refresh minor operation data after identity upload:',
              e,
            );
            if (docType === DocumentType.FamilyBook) throw e;
          }
        }

        navigate(ROUTE.MINOR_BASIC_INFO, {
          replace: true,
        });
      } catch (uploadError) {
        const message =
          uploadError instanceof Error
            ? uploadError.message
            : `Failed to upload ${docType === DocumentType.FamilyBook ? 'Family Book' : 'birth certificate'}`;
        setError(message);
        Bugfender.error('Minor identity upload failed:', uploadError);
      } finally {
        setIsUploading(false);
      }
    },
    [minorClientId, operationId, accountTypes, navigate, dispatch, docType],
  );

  return {
    upload,
    isUploading,
    error,
    resetError,
  };
}

export function useBirthCertificateUpload() {
  return useMinorIdentityUpload(DocumentType.BirthCertificate);
}
